import axios from "axios";
import { adminApi } from "../../lib/axios";

export type EmailValidationStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "failed"
  | null;

export interface ValidatedEmail {
  email: string;
  result: "valid" | "invalid" | "catchall" | "unknown" | "disposable";
  flags: string[];
  suggestedCorrection: string;
  validatedAt: string;
}

export interface Lead {
  id: string;
  leadId: string | null;
  domain: string;
  companyName: string | null;
  description: string | null;
  vertical: string | null;
  productCategories: string | null;
  employeeCount: number | null;
  websiteCreatedAt: string | null;
  language: string | null;
  passwordProtected: boolean | null;
  emails: any | null;
  phone: string | null;
  contactAvailable: boolean | null;
  emailCount: number | null;
  phoneCount: number | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zipCode: string | null;
  timezone: string | null;
  socialMedia: any | null;
  platform: string | null;
  theme: string | null;
  currency: string | null;
  productCount: number | null;
  variantCount: number | null;
  averageProductPrice: number | null;
  estimatedMonthlyRevenue: number | null;
  estimatedTechSpend: number | null;
  estimatedAppSpend: number | null;
  trafficAnalytics: any | null;
  technologies: any | null;
  shopifyApps: any | null;
  activitySignals: any | null;
  source: string;
  csvImportId: string | null;
  leadScore: number | null;
  emailSentAt: string | null;
  emailStatus: string | null;
  enrichedAt: string | null;
  phoneContactedAt: string | null;
  // Email validation
  validatedEmails: ValidatedEmail[] | null;
  emailValidationStatus: EmailValidationStatus;
  emailValidationJobId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ValidationTriggerResponse {
  jobId: string;
  nbJobId: number;
  status: "pending";
  totalEmails: number;
  leadCount: number;
}

export interface SavedFilter {
  id: string;
  name: string;
  description: string | null;
  filters: Record<string, any>;
  options: Record<string, any> | null;
  createdById: string;
  isDefault: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadsSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  country?: string;
  vertical?: string | string[];
  source?: string;
  contactType?: "email" | "phone" | "both" | "any";
  sortBy?: string;
  sortDir?: "asc" | "desc";
  savedFilterId?: string;
}

export interface LeadsResponse {
  data: Lead[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface LeadStats {
  totalLeads: number;
  leadsBySource: { source: string; count: number }[];
  leadsByCountry: { country: string; count: number }[];
}

export interface GenerateLeadsResponse {
  imported: number;
  skipped: number;
  fetched: number;
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
  savedFilter: { id: string; name: string } | null;
}

export interface StoreCensusApp {
  app_id: number;
  name: string;
  handle: string;
  description: string;
  icon_url: string;
  rating: number;
  developer: string;
  active: boolean;
  main_category: { category_id: number; name: string; slug: string };
  categories: { category_id: number; name: string; slug: string }[];
}

export interface StoreCensusAppCategory {
  category_id: number;
  name: string;
  slug: string;
  app_count: number;
}

export interface EnrichLeadsResponse {
  enriched: number;
  skippedNoLeadId: number;
  skippedNoContact: number;
  creditsDeducted: number;
  credits: { remaining: number; subscription: number; addon: number } | null;
  leads: Lead[];
  validationJob: ValidationTriggerResponse | null;
}

export interface SendEmailResponse {
  sent: number;
  skipped: number;
  failed: number;
  leadsWithoutEmails: string[];
  skippedSuppressed: number;
  skippedUndeliverable: number;
  skippedCap: number;
  skippedDuplicate: number;
}

export interface ContactLog {
  id: string;
  leadId: string;
  method: string;
  note: string | null;
  contactedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

class AdminLeadsService {
  async generateLeads(
    filters: Record<string, any>,
    options?: {
      pageSize?: number;
      cursor?: string;
      sort?: { column: string; direction: "asc" | "desc" };
      sections?: string[];
      saveAsName?: string;
    }
  ): Promise<GenerateLeadsResponse> {
    try {
      const response = await adminApi.post("/leads/generate-store-census", {
        filters,
        ...options,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to generate leads"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getLeads(params: LeadsSearchParams = {}): Promise<LeadsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);
      if (params.country) queryParams.append("country", params.country);
      if (params.vertical) {
        const vertical =
          Array.isArray(params.vertical) ? params.vertical.join(",") : params.vertical;
        queryParams.append("vertical", vertical);
      }
      if (params.source) queryParams.append("source", params.source);
      if (params.contactType) queryParams.append("contactType", params.contactType);
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortDir) queryParams.append("sortDir", params.sortDir);
      if (params.savedFilterId) queryParams.append("savedFilterId", params.savedFilterId);

      const response = await adminApi.get(
        `/leads?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch leads"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getLeadStats(): Promise<LeadStats> {
    try {
      const response = await adminApi.get("/leads/stats");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch lead stats"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getLeadById(id: string): Promise<Lead> {
    try {
      const response = await adminApi.get(`/leads/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch lead"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async enrichLeads(leadIds: string[]): Promise<EnrichLeadsResponse> {
    try {
      const response = await adminApi.post("/leads/enrich", { leadIds });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to enrich leads"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getSavedFilters(): Promise<SavedFilter[]> {
    try {
      const response = await adminApi.get("/leads/saved-filters");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch saved filters"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async createSavedFilter(dto: {
    name: string;
    description?: string;
    filters: Record<string, any>;
    options?: Record<string, any>;
    isDefault?: boolean;
  }): Promise<SavedFilter> {
    try {
      const response = await adminApi.post("/leads/saved-filters", dto);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to create saved filter"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async updateSavedFilter(
    id: string,
    dto: {
      name?: string;
      description?: string;
      filters?: Record<string, any>;
      options?: Record<string, any>;
      isDefault?: boolean;
    }
  ): Promise<SavedFilter> {
    try {
      const response = await adminApi.patch(
        `/leads/saved-filters/${id}`,
        dto
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update saved filter"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async deleteSavedFilter(id: string): Promise<void> {
    try {
      await adminApi.delete(`/leads/saved-filters/${id}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to delete saved filter"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async runSavedFilter(
    id: string,
    options?: { cursor?: string; pageSize?: number }
  ): Promise<GenerateLeadsResponse> {
    try {
      const body: Record<string, unknown> = {};
      if (options?.cursor !== undefined) body.cursor = options.cursor;
      if (options?.pageSize !== undefined) body.pageSize = options.pageSize;
      const response = await adminApi.post(
        `/leads/saved-filters/${id}/run`,
        body
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to run saved filter"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getApps(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    categoryId?: number;
  }): Promise<{
    data: StoreCensusApp[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
      hasMore: boolean;
    };
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.pageSize)
        queryParams.append("pageSize", params.pageSize.toString());
      if (params?.search) queryParams.append("search", params.search);
      if (params?.categoryId)
        queryParams.append("categoryId", params.categoryId.toString());

      const response = await adminApi.get(
        `/leads/apps?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch apps"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getPurgeDates(): Promise<{ date: string; count: number }[]> {
    try {
      const response = await adminApi.get("/leads/purge-dates");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch purge dates"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async purgeByDate(date: string): Promise<{ deleted: number }> {
    try {
      const response = await adminApi.delete(
        `/leads/purge?date=${encodeURIComponent(date)}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to purge leads"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getAppCategories(): Promise<{
    data: StoreCensusAppCategory[];
    total: number;
  }> {
    try {
      const response = await adminApi.get("/leads/app-categories");
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch app categories"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async sendEmail(
    leadIds: string[],
    selectedEmails?: Record<string, string[]>
  ): Promise<SendEmailResponse> {
    try {
      const response = await adminApi.post("/leads/send-email", {
        leadIds,
        selectedEmails,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to send emails"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getContactLogs(leadId: string): Promise<ContactLog[]> {
    try {
      const response = await adminApi.get(`/leads/${leadId}/contact-logs`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch contact logs"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async createContactLog(
    leadId: string,
    method: string,
    note?: string
  ): Promise<ContactLog> {
    try {
      const response = await adminApi.post(`/leads/${leadId}/contact-log`, {
        method,
        note,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to create contact log"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async pollValidationJob(jobId: string): Promise<any> {
    try {
      const response = await adminApi.get(`/leads/validation-jobs/${jobId}/status`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to poll validation job"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }
}

export const adminLeadsService = new AdminLeadsService();
